import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { RootState } from "app/store";
import type { LoadStatus } from "features/common/LoadStatus";
import {
    getAgentScoringRulesMaxOuterLimit,
    getEvaluationFormAiScoringQuestionsMaxOuterLimit,
} from "util/outerLimitUtils";

type OuterLimitState = {
    evaluationFormAiScoringQuestionsMax: number | undefined;
    agentScoringRulesMax: number | undefined;
    aiScoringQuestionsMaxStatus: LoadStatus;
    agentScoringRulesMaxStatus: LoadStatus;
};

const initialState: OuterLimitState = {
    evaluationFormAiScoringQuestionsMax: undefined,
    agentScoringRulesMax: undefined,
    aiScoringQuestionsMaxStatus: "idle",
    agentScoringRulesMaxStatus: "idle",
};

/**
 * Loads organization outer limit for AI scoring questions max (quality namespace).
 * Dispatched once when entering the evaluation form page — not tied to form field updates.
 */
export const loadMaxAiScoringQuestionsOuterLimit = createAsyncThunk(
    "outerLimit/loadMaxAiScoringQuestionsOuterLimit",
    async () => {
        return await getEvaluationFormAiScoringQuestionsMaxOuterLimit();
    },
    {
        condition: (_, { getState }): boolean => {
            const { aiScoringQuestionsMaxStatus } = (getState() as RootState)
                .outerLimit;
            return (
                aiScoringQuestionsMaxStatus !== "loading" &&
                aiScoringQuestionsMaxStatus !== "succeeded"
            );
        },
    }
);

/**
 * Loads organization outer limit for the max number of agent scoring rules
 * allowed per program (quality namespace). Dispatched once when entering the
 * agent scoring rules page.
 */
export const loadAgentScoringRulesMaxOuterLimit = createAsyncThunk(
    "outerLimit/loadAgentScoringRulesMaxOuterLimit",
    async () => {
        return await getAgentScoringRulesMaxOuterLimit();
    },
    {
        condition: (_, { getState }): boolean => {
            const { agentScoringRulesMaxStatus } = (getState() as RootState)
                .outerLimit;
            return (
                agentScoringRulesMaxStatus !== "loading" &&
                agentScoringRulesMaxStatus !== "succeeded"
            );
        },
    }
);

const outerLimitSlice = createSlice({
    name: "outerLimit",
    initialState,
    reducers: {},
    extraReducers(builder) {
        builder
            .addCase(loadMaxAiScoringQuestionsOuterLimit.pending, (state) => {
                state.aiScoringQuestionsMaxStatus = "loading";
            })
            .addCase(
                loadMaxAiScoringQuestionsOuterLimit.fulfilled,
                (state, { payload }) => {
                    state.aiScoringQuestionsMaxStatus = "succeeded";
                    state.evaluationFormAiScoringQuestionsMax = payload;
                }
            )
            .addCase(loadMaxAiScoringQuestionsOuterLimit.rejected, (state) => {
                state.aiScoringQuestionsMaxStatus = "failed";
                state.evaluationFormAiScoringQuestionsMax = undefined;
            })
            .addCase(loadAgentScoringRulesMaxOuterLimit.pending, (state) => {
                state.agentScoringRulesMaxStatus = "loading";
            })
            .addCase(
                loadAgentScoringRulesMaxOuterLimit.fulfilled,
                (state, { payload }) => {
                    state.agentScoringRulesMaxStatus = "succeeded";
                    state.agentScoringRulesMax = payload;
                }
            )
            .addCase(loadAgentScoringRulesMaxOuterLimit.rejected, (state) => {
                state.agentScoringRulesMaxStatus = "failed";
                state.agentScoringRulesMax = undefined;
            });
    },
});

export const selectEvaluationFormAiScoringQuestionsMaxOuterLimit = (
    state: RootState
) => state.outerLimit.evaluationFormAiScoringQuestionsMax;

export const selectAiScoringQuestionsMaxOuterLimitLoadStatus = (
    state: RootState
) => state.outerLimit.aiScoringQuestionsMaxStatus;

export const selectAgentScoringRulesMaxOuterLimit = (state: RootState) =>
    state.outerLimit.agentScoringRulesMax;

export const selectAgentScoringRulesMaxOuterLimitLoadStatus = (
    state: RootState
) => state.outerLimit.agentScoringRulesMaxStatus;

export default outerLimitSlice.reducer;
